/**
 * Line-item value helpers + the QuickBooks copy-out strings.
 *
 * Estimates are pasted into QuickBooks by hand — there is no sync. The cost
 * trace's "Customer price" node renders these two strings with copy buttons
 * (see traceModel.buildCustomerPriceNode and TraceNode's CopyField), and this
 * is now the only surface that offers them: the legacy results screen these
 * helpers came from has been deleted.
 *
 * MONEY RULE: nothing in this module derives a price. Where the engine reports
 * a total for the whole line, that total is what gets shown. Per-unit dollars x
 * line quantity is a FALLBACK only, for payloads that carry no line total —
 * because the engine's per-unit figures are already rounded to the cent, so
 * multiplying one by the quantity re-scales the rounding error and produces a
 * line that visibly disagrees with the job it belongs to.
 */

export function toNumberOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function toNumber(value, fallback = 0) {
  const parsed = toNumberOrNull(value);
  return parsed === null ? fallback : parsed;
}

/** Pieces on this line. Never zero — a line always prices at least one piece. */
export function getItemQuantity(item) {
  const quantity = toNumberOrNull(
    item?.input_data?.quantity ?? item?.quantity ?? 1,
  );
  return quantity !== null && quantity > 0 ? quantity : 1;
}

/** What the customer pays for one piece, straight from the backend. */
export function getFinalUnitPrice(item) {
  return toNumber(
    item?.final_customer_unit_price ??
      item?.components?.final_customer_unit_price,
  );
}

/**
 * The engine's own total for a line, read in preference order.
 *
 * Returns null when the payload carries none of the named fields, which is the
 * only condition under which a caller may fall back to per-unit x quantity.
 * Zero counts as an answer: a line the engine priced at nothing is not a line
 * with no price.
 */
export function getEngineLineTotal(item, ...fields) {
  if (!item) return null;
  for (const field of fields) {
    const direct = toNumberOrNull(item[field]);
    if (direct !== null) return direct;
    const nested = toNumberOrNull(item?.components?.[field]);
    if (nested !== null) return nested;
  }
  return null;
}

/**
 * Per-unit money x quantity — the fallback, never the first choice.
 * Kept as one named function so every call site is greppable.
 */
export function extendPerUnit(perUnit, quantity) {
  const parsed = toNumberOrNull(perUnit);
  if (parsed === null) return null;
  const qty = toNumberOrNull(quantity);
  return qty === null ? null : parsed * qty;
}

/* ── line labor hours ──────────────────────────────────────────────────────
   The engine reports hours PER PIECE, quantized to four decimals. An estimator
   who typed "196 hr for this line" gets 196 ÷ 8 = 24.5 back on a clean divide
   and 24.4583 on a dirty one — extending that per-piece figure then shows 195.67
   hr for a line that was entered as 196, and the dollars follow it by cents even
   though the job total is exact. So a line's hours are read in this order:
   what was entered for the line (exact, already a line total), then the engine's
   per-piece figure extended (the fallback, for lines priced by the rate card,
   which never had an entered figure to disagree with).                       */

const ENTERED_HOUR_FIELDS = ["cnc_hours", "cut_hours", "fab_hours"];

/**
 * Hours the estimator typed for the WHOLE line, or null when this line was not
 * hand-entered. Paint reads `finish_hours` first — the form's name for it —
 * falling back to `paint_hours`, the engine's.
 */
export function getEnteredLineLaborHours(item) {
  const input = item?.input_data;
  if (!input) return null;
  if (String(input.labor_source || "").toLowerCase() !== "manual") return null;

  const values = [
    ...ENTERED_HOUR_FIELDS.map((field) => toNumberOrNull(input[field])),
    toNumberOrNull(input.finish_hours ?? input.paint_hours),
  ];
  if (values.every((value) => value === null)) return null;
  return values.reduce((total, value) => total + (value || 0), 0);
}

/**
 * Hours for the whole line: entered hours when there are any, else the
 * engine's per-piece total extended by the pieces on the line.
 */
export function getLineLaborHours(item) {
  const entered = getEnteredLineLaborHours(item);
  if (entered !== null) return entered;
  const perPiece = toNumberOrNull(item?.labor_estimate_hours);
  if (perPiece === null) return null;
  return perPiece * getItemQuantity(item);
}

/**
 * What the customer pays for the whole line.
 *
 * The engine reports a line total for customer price on payloads that carry
 * one; only when it does not does this extend the (already rounded) unit price.
 */
export function getLineAmount(item) {
  const engineTotal = getEngineLineTotal(
    item,
    "final_customer_line_price",
    "final_customer_total_price",
  );
  if (engineTotal !== null) return engineTotal;

  const unitPrice = getFinalUnitPrice(item);
  const quantity = getItemQuantity(item);
  if (unitPrice > 0 && quantity > 0) return extendPerUnit(unitPrice, quantity);

  const calculatedTotal = toNumberOrNull(item?.calculated_total_cost);
  if (calculatedTotal !== null && calculatedTotal > 0) return calculatedTotal;

  return unitPrice;
}

/* ── what to call a line ───────────────────────────────────────────────────
   A custom assembly saved without a name comes back labelled "Custom
   Assembly" — a placeholder the backend supplies so the line has *something*
   to be called. Pasted into QuickBooks it tells the customer nothing, and on
   the trace header it makes two different lines read identically. But the line
   is not actually anonymous: its components are named (the backend now
   recovers a blank component's name from the material it points at), so the
   thing it is made of is the honest label.                                   */

const GENERIC_LINE_NAMES = new Set([
  "custom assembly",
  "custom line",
  "custom / one-off",
]);

function isGenericName(value) {
  return GENERIC_LINE_NAMES.has(
    String(value || "")
      .trim()
      .toLowerCase(),
  );
}

function text(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** Components for a line, preferring the priced results over the raw inputs. */
function lineComponents(item, lineItem = null) {
  const priced = item?.custom_component_results;
  if (Array.isArray(priced) && priced.length > 0) return priced;
  const fromResult = item?.input_data?.assembly_components;
  if (Array.isArray(fromResult) && fromResult.length > 0) return fromResult;
  const fromForm = lineItem?.assembly_components;
  return Array.isArray(fromForm) ? fromForm : [];
}

/**
 * A name for an unnamed assembly, taken from what it is made of: the first
 * material component, plus "assembly" when there is more than one component
 * (one component IS the thing; several make an assembly of it).
 */
export function deriveCustomLineName(item, lineItem = null) {
  const components = lineComponents(item, lineItem);
  if (components.length === 0) return null;
  const named = components.find(
    (component) => component?.type === "material" && text(component?.name),
  );
  const base = text(named?.name);
  if (!base) return null;
  return components.length > 1 ? `${base} assembly` : base;
}

/**
 * What this line should be called anywhere it is shown to a person: the name
 * someone typed, else the engine's product name, else what it is made of, and
 * only then the generic placeholder.
 */
export function getLineDisplayName(item, lineItem = null) {
  const typed = text(lineItem?.name) || text(item?.input_data?.name);
  if (typed) return typed;

  const productName = text(item?.product_name);
  if (productName && !isGenericName(productName)) return productName;

  return deriveCustomLineName(item, lineItem);
}

/**
 * The description an estimator pastes into a QuickBooks estimate line.
 *
 * REGRESSION RULE: a line with no description must produce a byte-identical
 * string to before description existed — the append only happens when
 * `input_data.description` is a non-empty string.
 */
export function buildQboLineDescription(item, index) {
  const itemNumber = (Number(index) || 0) + 1;
  const productName = getLineDisplayName(item) || `Line Item ${itemNumber}`;
  // A dimension is only worth pasting into QuickBooks when it was actually
  // entered. Custom lines carry all three fields defaulted to 0, so an unset
  // dimension must drop out rather than read as `0" W`.
  const formatDimension = (value, label) => {
    const parsed = toNumberOrNull(value);
    if (parsed === null || parsed <= 0) return null;
    return `${Number(parsed)}" ${label}`;
  };
  const dimensionParts = [
    formatDimension(item?.input_data?.length, "P"),
    formatDimension(item?.input_data?.height, "H"),
    formatDimension(item?.input_data?.width, "W"),
  ].filter(Boolean);
  const headline =
    dimensionParts.length > 0
      ? `${productName} ${dimensionParts.join(" x ")}`
      : productName;

  const description =
    typeof item?.input_data?.description === "string"
      ? item.input_data.description.trim()
      : "";
  return description ? `${headline}\n${description}` : headline;
}

/** The unit price as QuickBooks wants it typed: bare, two decimals. */
export function getQboUnitCostString(item) {
  const unitPrice = getFinalUnitPrice(item);
  return Number.isFinite(unitPrice) ? unitPrice.toFixed(2) : "0.00";
}

/* ── the whole job, in one paste ───────────────────────────────────────────
   Per line the trace hands over a description and a unit price. A twelve-line
   job is then twelve rail clicks and twenty-four copy clicks — and quantity,
   the field that multiplies the money, never leaves the app at all: it gets
   retyped from memory. This builds every priced line as tab-separated rows so
   the whole job pastes into a spreadsheet or a QuickBooks estimate grid at
   once, quantity included.

   Column order is description, quantity, unit price — a read of the QBO
   estimate line, and a one-line edit here if the estimators want it otherwise.

   NEWLINE RULE: buildQboLineDescription puts the estimator's own description
   on a second line, and a literal newline inside a cell ENDS THE ROW in every
   spreadsheet grid — one line would paste as two and every figure after it
   would sit against the wrong description. So inside the TSV, and only there,
   an inner break becomes an em-dash separator. The per-line copy strings keep
   their newline: those go into a single QuickBooks description field, which
   wants the break. A tab inside a value would split a cell the same way, so it
   collapses to a space.                                                     */

const TSV_INNER_BREAK = " — ";

function toTsvCell(value) {
  return String(value ?? "")
    .replace(/\r\n|\r|\n/g, TSV_INNER_BREAK)
    .replace(/\t/g, " ");
}

/**
 * Every priced line of a job as `description \t quantity \t unit price` rows.
 *
 * Lines that did not price carry an `error` and are skipped — pasting a $0.00
 * row for a line the engine refused would put a wrong number in front of a
 * customer. Their INDEX still counts, so an unnamed line keeps the same
 * "Line Item N" it wears everywhere else on the screen. Returns "" when there
 * is nothing to paste.
 */
export function buildQboJobTsv(lineItemResults) {
  if (!Array.isArray(lineItemResults)) return "";
  return lineItemResults
    .map((item, index) => {
      if (!item || item.error) return null;
      return [
        toTsvCell(buildQboLineDescription(item, index)),
        toTsvCell(getItemQuantity(item)),
        toTsvCell(getQboUnitCostString(item)),
      ].join("\t");
    })
    .filter((row) => row !== null)
    .join("\n");
}
